import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  getCurrentChileDateString,
  createChileTimestampAsUTC,
  formatChileTimeFromUTC,
} from "../utils/chileTimez";
import { useAuthSession } from "../hooks/useAuthSession";

export function MarkAttendanceCard() {
  const { user, getCurrentUser } = useAuthSession();

  // Igual que en asistencia.jsx: preferir user del hook y caer a getCurrentUser
  const usuarioActual = user || getCurrentUser?.() || null;
  const userId = usuarioActual?.id || null;


  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [loadingCheckIn, setLoadingCheckIn] = useState(false);
  const [loadingCheckOut, setLoadingCheckOut] = useState(false);

  const verificarAsistenciaHoy = useCallback(async () => {
    if (!userId) return;
    try {
      const today = getCurrentChileDateString();
      const { data, error } = await supabase
        .from("asistencias")
        .select("hora_entrada, hora_salida")
        .eq("user_id", userId)
        .eq("fecha", today)
        .order("hora_entrada", { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        setCheckInTime(formatChileTimeFromUTC(data.hora_entrada, true));
        if (data.hora_salida) {
          setCheckOutTime(formatChileTimeFromUTC(data.hora_salida, true));
          setIsCheckedIn(false);
        } else {
          setIsCheckedIn(true);
          setCheckOutTime(null);
        }
      } else {
        setIsCheckedIn(false);
        setCheckInTime(null);
        setCheckOutTime(null);
      }
    } catch (e) {
      console.error("[MarkAttendanceCard] Error al verificar asistencia:", e);
    }
  }, [userId]);

  useEffect(() => {
    verificarAsistenciaHoy();
  }, [verificarAsistenciaHoy]);

  const handleCheckIn = async () => {
    if (loadingCheckIn) return;
    if (!userId) {
      alert("Error: Usuario no autenticado");
      return;
    }

    setLoadingCheckIn(true);
    try {
      const today = getCurrentChileDateString();
      const { data: registroHoy, error: errorHoy } = await supabase
        .from("asistencias")
        .select("id")
        .eq("user_id", userId)
        .eq("fecha", today)
        .order("hora_entrada", { ascending: false })
        .limit(1)
        .single();

      if (registroHoy && !errorHoy) {
        alert(
          "Ya tienes una entrada registrada para hoy. No puedes marcar otra entrada."
        );
        setLoadingCheckIn(false);
        return;
      }

      const now = new Date();
      const fechaISO = today;
      const timestampEntrada = createChileTimestampAsUTC(now);

      const { error } = await supabase.from("asistencias").insert([
        {
          user_id: userId,
          fecha: fechaISO,
          hora_entrada: timestampEntrada,
          observaciones: null,
        },
      ]);

      if (error) {
        alert("Error al registrar la entrada: " + error.message);
        console.error(error);
      } else {
        await verificarAsistenciaHoy();
      }
    } catch (e) {
      console.error(
        "[MarkAttendanceCard] Error inesperado al registrar entrada:",
        e
      );
      alert("Error inesperado al registrar la entrada");
    } finally {
      setLoadingCheckIn(false);
    }
  };

  const handleCheckOut = async () => {
    if (loadingCheckOut) return;
    if (!userId) {
      alert("Error: Usuario no autenticado");
      return;
    }

    setLoadingCheckOut(true);
    try {
      const today = getCurrentChileDateString();
      const now = new Date();
      const timestampSalida = createChileTimestampAsUTC(now);

      const { data: registroHoy, error: searchError } = await supabase
        .from("asistencias")
        .select("id")
        .eq("user_id", userId)
        .eq("fecha", today)
        .is("hora_salida", null)
        .order("hora_entrada", { ascending: false })
        .limit(1)
        .single();

      if (searchError || !registroHoy) {
        alert("No se encontró un registro de entrada para actualizar");
        console.error(
          "[MarkAttendanceCard] Error al buscar registro:",
          searchError
        );
        setLoadingCheckOut(false);
        return;
      }

      const { error } = await supabase
        .from("asistencias")
        .update({ hora_salida: timestampSalida })
        .eq("id", registroHoy.id);

      if (error) {
        alert("Error al registrar la salida: " + error.message);
        console.error(error);
      } else {
        await verificarAsistenciaHoy();
      }
    } catch (e) {
      console.error(
        "[MarkAttendanceCard] Error inesperado al registrar salida:",
        e
      );
      alert("Error inesperado al registrar la salida");
    } finally {
      setLoadingCheckOut(false);
    }
  };

  if (!userId) {
    // Si por alguna razón no hay usuario cargado, no mostramos nada (evita parpadeos)
    return null;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">
            Asistencia Web
          </p>
          <h2 className="text-sm sm:text-base font-semibold text-slate-900">
            Marca tu jornada de hoy
          </h2>
        </div>
      </div>

      <div className="mb-3 text-xs text-slate-500 min-h-[1.5rem]">
        {isCheckedIn && checkInTime && (
          <p>Entrada registrada a las {checkInTime}</p>
        )}
        {!isCheckedIn && checkOutTime && (
          <p>Salida registrada a las {checkOutTime}</p>
        )}
        {!isCheckedIn && !checkOutTime && <p>No has marcado tu entrada hoy.</p>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleCheckIn}
          disabled={isCheckedIn || loadingCheckIn}
          className={`flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors ${
            isCheckedIn || loadingCheckIn
              ? "bg-emerald-300 cursor-not-allowed"
              : "bg-emerald-500 hover:bg-emerald-600"
          }`}
        >
          {loadingCheckIn ? "Guardando..." : "Marcar entrada"}
        </button>
        <button
          type="button"
          onClick={handleCheckOut}
          disabled={!isCheckedIn || loadingCheckOut}
          className={`flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium border shadow-sm transition-colors ${
            !isCheckedIn || loadingCheckOut
              ? "border-emerald-200 text-emerald-300 bg-emerald-50 cursor-not-allowed"
              : "border-emerald-500 text-emerald-600 hover:bg-emerald-50"
          }`}
        >
          {loadingCheckOut ? "Guardando..." : "Marcar salida"}
        </button>
      </div>
    </div>
  );
}
